import React, { useEffect, useRef, useState } from 'react'
import { Box, useMediaQuery, useTheme } from '@mui/material'

import { darkTheme, lightTheme } from '~createTheme'
import { EnrichedOrganizationAISettings } from '~types'

interface AISearchBarProps {
  suggestions?: string[]
  hasResults?: boolean
  onSearch: (query: string) => void
  query: string
  onQueryChange: (query: string) => void
  inputRef?: React.RefObject<HTMLTextAreaElement>
  orgAiSettings?: EnrichedOrganizationAISettings
}

const AISearchBar: React.FC<AISearchBarProps> = ({
  suggestions = [],
  hasResults = false,
  onSearch,
  query,
  onQueryChange,
  inputRef,
  orgAiSettings,
}) => {
  // Derive theme from URL parameters
  const searchParams = new URLSearchParams(window?.location?.search || '')
  const themeMode = searchParams.get('theme')
  const theme = themeMode === 'dark' ? darkTheme : lightTheme

  const muiTheme = useTheme()
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'))

  const [showSuggestions, setShowSuggestions] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const defaultInputRef = useRef<HTMLTextAreaElement>(null)
  const effectiveInputRef = inputRef || defaultInputRef

  const normalizedSuggestions = (suggestions ?? []).map((s) => s?.trim() ?? '').filter(Boolean)
  const placeholderText = normalizedSuggestions.length > 0 ? suggestions[0] : 'Ask a question'
  const remainingSuggestions = normalizedSuggestions.slice(1)

  const postMessageScrollHeightOneLine = (): void =>
    window.parent.postMessage({ scrollHeight: document.body.scrollHeight }, '*')

  useEffect(() => {
    postMessageScrollHeightOneLine()
  }, [showSuggestions])

  const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
    onQueryChange(event.target.value)
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    if (query.trim()) {
      onSearch(query.trim())
      setShowSuggestions(false)
    }
  }

  const handleSuggestionClick = (suggestion: string): void => {
    const trimmed = suggestion.trim()
    onQueryChange(trimmed)
    onSearch(trimmed)
    setShowSuggestions(false)
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (event.key === 'Enter') {
      event.preventDefault()
      if (query.trim()) {
        onSearch(query.trim())
        setShowSuggestions(false)
      }
    }
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return (): void => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <Box ref={containerRef}>
      <Box
        component='form'
        onSubmit={handleSubmit}
        sx={{
          color: theme.palette.text.primary,
          display: 'flex',
          alignItems: 'flex-start',
          width: '100%',
          borderLeft: `1px solid ${theme.palette.aiSearch.border}`,
          borderRight: `1px solid ${theme.palette.aiSearch.border}`,
          borderBottom: `1px solid ${theme.palette.aiSearch.border}`,
          justifyContent: 'space-between',
          py: 1,
        }}
      >
        <img
          src='https://s3-us-east-2.amazonaws.com/polimorphic-sha256/assets/search.svg'
          alt='search-icon'
          style={{
            filter: themeMode !== 'dark' ? 'brightness(0)' : 'brightness(0.1) invert(1)',
            margin: '24px',
            width: '20px',
            alignSelf: 'center',
          }}
        />
        <Box
          component='textarea'
          ref={effectiveInputRef}
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (!hasResults) {
              setShowSuggestions(true)
            }
          }}
          placeholder={placeholderText}
          rows={1}
          sx={{
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.aiSearch.searchBackground,
            fontSize: '18px',
            padding: '20px 20px 20px',
            resize: 'none',
            width: '100%',
            height: '100%',
            border: 'none',
            outline: 'none',
            fontFamily: orgAiSettings?.font_family || 'inherit',
            ...(isMobile && {
              minHeight: '100px',
              alignContent: 'center',
              lineHeight: '1.4',
            }),
            '&:hover': {
              boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
            },
            '&:focus': {
              outlineOffset: '2px',
              boxShadow: `0 0 0 2px ${theme.palette.primary.main}, 0 0 0 4px #98e0ff`,
            },
          }}
          aria-label='search'
          aria-description='search results will appear below'
          role='searchbox'
          tabIndex={0}
        />
        <button
          type='submit'
          className='double-blue-border-on-focus touch-target flex-vh-align'
          style={{
            backgroundColor: theme.palette.text.primary,
            borderRadius: `${orgAiSettings?.corner_radius ?? 15}px`,
            cursor: 'pointer',
            height: '36px',
            margin: '24px',
            minWidth: '36px',
            width: '36px',
            border: 'none',
            alignSelf: 'center',
          }}
          aria-label='submit search'
        >
          <img
            src='https://s3-us-east-2.amazonaws.com/polimorphic-sha256/assets/forward.svg'
            alt='submit-icon'
            style={{
              filter: themeMode !== 'dark' ? 'brightness(0) invert(1)' : 'brightness(0)',
              width: '15px',
            }}
          />
        </button>
      </Box>

      {!hasResults && showSuggestions && remainingSuggestions.length > 0 && (
        <Box
          sx={{
            borderBottom: `1px solid ${theme.palette.aiSearch.border}`,
            borderLeft: `1px solid ${theme.palette.aiSearch.border}`,
            borderRight: `1px solid ${theme.palette.aiSearch.border}`,
          }}
        >
          {remainingSuggestions.map((suggestion, index) => (
            <Box key={index}>
              <Box
                component='button'
                tabIndex={0}
                onClick={() => handleSuggestionClick(suggestion)}
                sx={{
                  color: theme.palette.text.primary,
                  borderTop: `1px solid ${theme.palette.aiSearch.border}`,
                  cursor: 'pointer',
                  fontSize: '18px',
                  padding: '20px 20px 20px 70px',
                  textAlign: 'left',
                  width: '100%',
                  backgroundColor: 'transparent',
                  border: 'none',
                  fontFamily: orgAiSettings?.font_family || 'inherit',
                  '&:hover, &:focus': {
                    backgroundColor: theme.palette.aiSearch.hover,
                  },
                }}
              >
                {suggestion}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}

export default AISearchBar
